<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import OrderReturn from "@/services/westore/entities/orderReturn";
import moment from "moment";
import { Download } from "@/mixins/download";
import WsTable from "../../../components/westoreTable/ws-table.vue";
import SkeletonLoader from "../../../components/skeletons/skeleton-loader.vue";

export default {
  mixins: [Download],
  page: {
    title: "Returns List",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    WsTable,
    SkeletonLoader,
  },
  data() {
    return {
      title: "Returns List",
      filter: {
        date_from: null,
        date_to: null,
        status: null,
      },
      sortBy: "id",
      sortDesc: false,
      currentPage: 1,
      perPage: 20,
      totalItems: 0,
      items: [],
      paginateStart: 1,
      fields: [
        {
          key: "order_return_number",
          label: this.$t("Return"),
          sortable: true,
        },
        {
          key: "order_number",
          label: this.$t("Order"),
          sortable: true,
        },
        {
          key: "recipient.name",
          label: this.$t("CUSTOMER NAME"),
          sortable: true,
        },
        {
          key: "skus",
          label: this.$t("SKU"),
          sortable: true,
        },
        {
          key: "created_at",
          label: this.$t("CREATED AT"),
          sortable: true,
        },
        {
          key: "status",
          label: this.$t("Status"),
          sortable: true,
        },
        {
          key: "channel",
          label: this.$t("CHANNEL"),
          sortable: true,
        },
        {
          key: "actions",
          label: this.$t("ACTIONS"),
          sortable: false,
        },
      ],
      showContentLoader: true,
    };
  },
  computed: {},
  mounted() {
    this.fetchData();
  },
  watch: {
    currentPage: {
      handler: function () {
        this.fetchData();
      },
    },
  },
  methods: {
    async fetchData() {
      this.showContentLoader = true;
      const requestParams = {
        ...this.filter,
        page: this.currentPage,
        per_page: this.perPage,
      };

      let resp = await OrderReturn.getAll(requestParams);
      this.items = resp.data;
      this.totalItems = resp.meta.total;
      this.showContentLoader = false;
    },
    applyFilter() {
      this.currentPage = 1;
      this.fetchData();
    },
    resetFilter() {
      this.currentPage = 1;
      this.filter = {
        date_from: null,
        date_to: null,
        status: null,
      };
      this.fetchData();
    },
    convertDate: function (timestampValue) {
      return moment.unix(timestampValue).format("DD/MM/YYYY");
    },
    async exportSheet(extension) {
      const data = await OrderReturn.blobResponse().export(
        extension,
        this.filter
      );

      const type = OrderReturn.getHeader("content-type");
      const filename = `order_return_${new Date().getTime()}.${extension}`;
      this.download(data, type, filename);
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title"
      ><b-button
        type="submit"
        variant="primary"
        class="btn-block"
        size="md"
        @click="$router.push('/order-returns/create')"
        ><i :class="isRtl ? 'fa fa-plus ml-3' : 'fa fa-plus mr-3'"></i
        >{{ $t("Create new") }}</b-button
      ></PageHeader
    >

    <!-- search -->
    <div class="row">
      <!-- Print & Export -->
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Table -->
            <div class="row mb-5">
              <div class="col-xl-2 col-sm-6 pt-0">
                <div class="form-group mb-0">
                  <label>{{ $t("Date From") }}</label>
                  <b-form-input
                    v-model="filter.receiving_date_from"
                    type="date"
                  ></b-form-input>
                </div>
              </div>

              <div class="col-xl-2 col-sm-6 pt-0">
                <div class="form-group mb-0">
                  <label>{{ $t("Date To") }}</label>
                  <b-form-input
                    v-model="filter.receiving_date_to"
                    type="date"
                  ></b-form-input>
                </div>
              </div>

              <div class="col-xl-2 col-sm-6 pt-0">
                <div class="form-group mb-0">
                  <label>{{ $t("Status") }}</label>
                  <select
                    class="form-control select2-search-disable"
                    v-model="filter.status"
                  >
                    <option
                      v-for="{ name, value } in orderStatuses"
                      :key="value"
                      :value="value"
                    >
                      {{ $t(name) }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="col-xl col-sm-6 align-self-end pb-3">
                <div class="mt-0 d-flex justify-end align-center">
                  <div :class="isRtl ? 'ml-auto' : 'mr-auto'">
                    <v-btn color="primary_1" dark @click="applyFilter">
                      <i class="fa fa-search"></i>
                    </v-btn>
                    <v-btn
                      class="mx-2"
                      color="warning"
                      dark
                      @click="resetFilter"
                    >
                      <i class="fa fa-trash"></i>
                    </v-btn>
                  </div>
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="primary"
                        dark
                        v-bind="attrs"
                        v-on="on"
                        outlined
                      >
                        {{ $t("Export") }}
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click.prevent="exportSheet('pdf')">
                        <v-list-item-title>
                          <i class="fa fa-file-pdf mr-2"></i>
                          Pdf</v-list-item-title
                        >
                      </v-list-item>
                      <v-list-item @click.prevent="exportSheet('xlsx')">
                        <v-list-item-title
                          ><i class="fa fa-file-excel mr-2"></i
                          >Excel</v-list-item-title
                        >
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </div>
            <SkeletonLoader type="table" v-if="showContentLoader" />
            <div v-else>
              <WsTable
                :items="items"
                :totalItems="totalItems"
                :fields="fields"
                :showPage="'order-return-details'"
                :paginateStart="paginateStart"
              />
              <div class="row">
                <div class="col">
                  <div
                    class="dataTables_paginate paging_simple_numbers float-end"
                  >
                    <ul class="pagination pagination-rounded mb-0">
                      <!-- pagination -->
                      <b-pagination
                        v-model="currentPage"
                        :total-rows="totalItems"
                        :per-page="perPage"
                        :first-text="this.$t('First')"
                        :prev-text="this.$t('Prev')"
                        :next-text="this.$t('Next')"
                        :last-text="this.$t('Last')"
                      ></b-pagination>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
<style lang="scss" scoped>
.p-item + .p-item:before {
  content: ", ";
}
</style>
